<script setup>
import { ref, nextTick, defineProps, defineEmits, defineExpose, watch, computed } from 'vue';
import CheckBox from '../ui/CheckBox.vue';
import { triggerContentUpdated } from '../../utils/magentoDom';

const props = defineProps({
    initialFilters: { type: Object, required: true },
    availableTastes: { type: Array, default: () => [] },
    availableBrands: { type: Array, default: () => [] },
});

const emit = defineEmits(['filter-changed']);

const selectedBrands = ref([]);
const selectedTastes = ref([]);
const resultsHtml = ref('');

// по умолчанию раскрыто
const isTastesOpen = ref(true);

// чтобы не эмитить фильтры во время инициализации из props
const isSyncingFromProps = ref(true);

// чтобы “Выбрать все” инициализировалось только первый раз
const hasInitializedTastes = ref(false);

// названия брендов
const BRAND_TITLES = {
    pro_plan: 'PRO PLAN®',
    purina_one: 'Purina ONE®',
};

// доступные бренды (только те, что пришли)
const availableBrands = computed(() => {
    return (props.availableBrands || [])
        .map(String)
        .filter(Boolean)
        .map((value) => ({
            value,
            title: BRAND_TITLES[value] || value,
        }));
});

// доступные вкусы (только те, что пришли)
const availableTastes = computed(() => {
    return (props.availableTastes || []).map(String).filter(Boolean);
});

// “Выбрать все” считает выбранными все доступные вкусы
const isAllTastesSelected = computed(() => {
    const all = availableTastes.value || [];
    if (all.length === 0) return false;
    const selected = selectedTastes.value || [];
    const set = new Set(selected.map(String));
    return all.every((t) => set.has(String(t)));
});

// проверка на наличие продуктов в HTML
const hasProducts = computed(() => {
    const html = resultsHtml.value || '';
    return /<li\b/i.test(html);
});

// синхронизируем state из props.initialFilters
watch(
    () => props.initialFilters,
    (val) => {
        isSyncingFromProps.value = true;

        selectedBrands.value = Array.isArray(val?.brands) ? [...val.brands] : [];
        selectedTastes.value = Array.isArray(val?.taste) ? [...val.taste] : [];

        nextTick(() => {
            isSyncingFromProps.value = false;
        });
    },
    { immediate: true, deep: true }
);

// инициализация “выбрать все” только один раз
watch(
    () => availableTastes.value,
    (list) => {
        if (!hasInitializedTastes.value && Array.isArray(list) && list.length) {
            selectedTastes.value = [...list];
            hasInitializedTastes.value = true;
            // не вызываем emitFilters здесь, поиск уже сделан
        }
    },
    { immediate: true }
);

// обновление html результатов
const hasEverLoaded = ref(false);

const updateResultsHtml = (html) => {
    hasEverLoaded.value = true;
    resultsHtml.value = html;

    nextTick(() => {
        triggerContentUpdated('#search-results-container');
    });
};

const showEmpty = computed(() => hasEverLoaded.value && !hasProducts.value);

defineExpose({ updateResultsHtml });

// переключаем показывать/скрывать список вкусов
const toggleTastes = () => (isTastesOpen.value = !isTastesOpen.value);

// эмитим только когда пользователь реально меняет фильтры
const emitFilters = () => {
    if (isSyncingFromProps.value) return;

    emit('filter-changed', {
        brands: [...selectedBrands.value],
        taste: [...selectedTastes.value],
    });
};

// кнопка “Выбрать все” / “Сбросить”
const toggleSelectAllTastes = () => {
    if (isSyncingFromProps.value) return;

    isSyncingFromProps.value = true;

    selectedTastes.value = isAllTastesSelected.value
        ? []
        : [...availableTastes.value];

    nextTick(() => {
        isSyncingFromProps.value = false;
        emitFilters();
    });
};
</script>

<template>
    <div class="feed-picker-results">
        <div class="feed-picker__container">
            <div class="feed-picker-filters__wrap">

                <!-- BRANDS: показываем ТОЛЬКО если они есть -->
                <div
                    v-if="availableBrands.length"
                    class="feed-picker-filters _brands"
                >
                    <div class="feed-picker-filters__list">
                        <div
                            v-for="brand in availableBrands"
                            :key="brand.value"
                            class="feed-picker-filters__list-item"
                        >
                            <CheckBox
                                :name="'brands'"
                                :text="brand.title"
                                :value="brand.value"
                                v-model="selectedBrands"
                                @update:modelValue="emitFilters"
                            />
                        </div>
                    </div>
                </div>

                <!-- TASTES -->
                <div v-if="availableTastes.length" class="feed-picker-filters _tastes">
                    <div class="feed-picker-filters__header">
                        <div class="feed-picker-filters__title">Вкусовые предпочтения</div>

                        <div
                            :class="['feed-picker-filters__arrow', { expanded: isTastesOpen }]"
                            @click="toggleTastes"
                        />
                    </div>

                    <transition name="slide-fade">
                        <div v-show="isTastesOpen" class="feed-picker-filters__list">

                            <!-- Выбрать все -->
                            <div v-if="availableTastes.length" class="feed-picker-filters__list-item _select-all">
                                <label class="checkbox" :class="{ _checked: isAllTastesSelected }">
                                    <input
                                        type="checkbox"
                                        name="taste_select_all"
                                        :checked="isAllTastesSelected"
                                        @change="toggleSelectAllTastes"
                                    >
                                    <span class="checkbox__text"><p>Выбрать все</p></span>
                                </label>
                            </div>

                            <!-- список вкусов -->
                            <div
                                v-for="taste in availableTastes"
                                :key="taste"
                                class="feed-picker-filters__list-item"
                            >
                                <CheckBox
                                    :name="'taste'"
                                    :text="taste"
                                    :value="taste"
                                    v-model="selectedTastes"
                                    @update:modelValue="emitFilters"
                                />
                            </div>

                            <!-- если нет вкусов -->
                            <div v-if="availableTastes.length === 0" class="feed-picker-filters__empty">
                                Нет доступных вкусов
                            </div>

                        </div>
                    </transition>
                </div>

            </div>
        </div>

        <div class="feed-picker-results__products">
            <div class="feed-picker__container">
                <div id="search-results-container" class="results-container" v-html="resultsHtml"></div>

                <div v-if="showEmpty" class="feed-picker-results__empty">
                    Ничего не найдено.
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}
</style>
